//----------------------
// OFAC Maintenance Service SDK
// Generated to match OfacMaintenanceClient.cs
//----------------------

/* tslint:disable */
/* eslint-disable */

import { Injectable, Inject, Optional, InjectionToken } from '@angular/core';
import { HttpClient, HttpHeaders, HttpResponse, HttpResponseBase } from '@angular/common/http';
import { Observable, throwError, of, mergeMap, catchError } from 'rxjs';
import { environment } from '../../environments/environment';

export const API_BASE_URL = new InjectionToken<string>('API_BASE_URL');

// ==================== Models ====================

export interface OfacAuditModel {
  applicationId?: string | null;
  contactId?: string | null;
  contactName?: string | null;
  createdOn?: Date | null;
  createdBy?: string | null;
  ofacAuditId: string;
  ofacContactId?: number | null;
  ofacMatchTypeId?: number | null;
  resultJson?: string | null;
  searchJson?: string | null;
  sourceId?: string | null;
}

export interface OfacExclusionModel {
  ofacExclusionId: string;
  applicationId?: string | null;
  contactId?: string | null;
  sourceId?: string | null;
  ofacContactId?: number | null;
  excludedOn?: Date | null;
  excludedBy?: string | null;
}

export interface OfacApplicationModel {
  ofacApplicationId: string;
  abbreviation: string;
  description: string;
}

export interface FlaggedContactModel {
  applicationId?: string | null;
  address: string;
  city: string;
  contactId: string;
  dateExcluded?: Date | null;
  excludedBy: string;
  fullName: string;
  sourceId: string;
  state: string;
}

export interface FlaggedContactDetailModel {
  addresses?: OfacAddressModel[] | null;
  aliases?: OfacAliasModel[] | null;
  applicationId?: string | null;
  audit?: OfacAuditModel | null;
  contactId: string;
  exclusionId: string;
  firstName: string;
  lastName: string;
  middleNames: string;
  ofacContactId: string;
}

export interface OfacAddressModel {
  address?: string | null;
  cityState?: string | null;
}

export interface OfacAliasModel {
  firstName?: string | null;
  lastName?: string | null;
  middleNames?: string | null;
}

export interface OfacSettingModel {
  acaNumber: string;
  associationName: string;
  matchTypes?: number[] | null;
  ofacNotificationEmailRecipients: string;
  profileDirectory: string;
  sendOfacContactCheckJobReportToOnBase?: boolean | null;
}

export interface AuditDetailModel {
  applicationName: string;
  confidencePercent: string;
  contactId: string;
  contactName: string;
  createdBy: string;
  createdOn?: Date | null;
  ofacContactId: string;
  ofacMatchTypeName: string;
  resultJson: string;
  searchJson: string;
}

export interface ServiceBusModel {
  acaNumber: string;
  contactDbId: string;
  correlationId: string;
  isMatch: boolean;
}

// ==================== IClient Interface ====================

export interface IOfacMaintenanceClient {
  /**
   * Logs an OFAC match result for audit and compliance tracking purposes.
   */
  logOfacMatchAsync(model: OfacAuditModel): Observable<OfacAuditModel>;

  /**
   * Retrieves all audit records from the OFAC service.
   */
  getAuditRecordsAsync(): Observable<OfacAuditModel[]>;

  /**
   * Retrieves audit records for a specific source from the OFAC service.
   */
  getAuditRecordsAsyncBySource(sourceId: string): Observable<OfacAuditModel[]>;

  /**
   * Retrieves audit records for a specific source and application from the OFAC service.
   */
  getAuditRecordsAsyncBySourceAndApplication(sourceId: string, applicationId: string): Observable<OfacAuditModel[]>;

  /**
   * Retrieves a specific exclusion record by identifier from the OFAC service.
   */
  getExclusionRecordAsync(id: string): Observable<OfacExclusionModel[]>;

  /**
   * Retrieves all exclusion records from the OFAC service.
   */
  getExclusionRecordsAsync(): Observable<OfacExclusionModel[]>;

  /**
   * Retrieves exclusion records for a specific source from the OFAC service.
   */
  getExclusionRecordsAsyncBySource(sourceId: string): Observable<OfacExclusionModel[]>;

  /**
   * Retrieves exclusion records for a specific source and application from the OFAC service.
   */
  getExclusionRecordsAsyncBySourceAndApplication(sourceId: string, applicationId: string): Observable<OfacExclusionModel[]>;

  /**
   * Retrieves exclusion records for a specific source, application, and contact from the OFAC service.
   */
  getExclusionRecordsAsyncBySourceApplicationAndContact(sourceId: string, applicationId: string, contactId: string): Observable<OfacExclusionModel[]>;

  /**
   * Adds an exclusion record to the OFAC service for entity filtering.
   */
  addExclusionAsync(model: OfacExclusionModel): Observable<OfacExclusionModel>;

  /**
   * Deletes an exclusion record from the OFAC service.
   */
  deleteExclusionAsync(id: string): Observable<void>;

  /**
   * Retrieves all application records from the OFAC service.
   */
  getApplicationRecordsAsync(): Observable<OfacApplicationModel[]>;

  /**
   * Retrieves a specific application record by identifier from the OFAC service.
   */
  getApplicationRecordAsync(id: string): Observable<OfacApplicationModel[]>;

  /**
   * Adds an application record to the OFAC service.
   */
  addApplicationAsync(model: OfacApplicationModel): Observable<OfacApplicationModel>;

  /**
   * Deletes an application record from the OFAC service.
   */
  deleteApplicationAsync(id: string): Observable<void>;

  /**
   * Retrieves flagged contacts for a specific source from the OFAC service.
   */
  getFlaggedContactsAsync(sourceId: string): Observable<FlaggedContactModel[]>;

  /**
   * Retrieves flagged contact exclusions for a specific source from the OFAC service.
   */
  getFlaggedContactExclusionsAsync(sourceId: string): Observable<FlaggedContactModel[]>;

  /**
   * Retrieves non-excluded OFAC match detail rows for a specific source and contact.
   */
  getFlaggedContactDetailsAsync(sourceId: string, contactId: string): Observable<FlaggedContactDetailModel[]>;

  /**
   * Retrieves excluded OFAC match detail rows for a specific source and contact.
   */
  getExcludedContactDetailsAsync(sourceId: string, contactId: string): Observable<FlaggedContactDetailModel[]>;

  /**
   * Retrieves the date when the OFAC database was last loaded from external sources.
   */
  getLastLoadDateAsync(): Observable<Date>;

  /**
   * Retrieves the total count of OFAC records currently stored in the database.
   */
  getRecordCountAsync(): Observable<number>;

  /**
   * Refreshes the OFAC database by loading the latest sanctions information from external sources.
   */
  refreshDataBaseAsync(): Observable<void>;

  /**
   * Retrieves all OFAC settings.
   */
  getAllSettingsAsync(): Observable<OfacSettingModel[]>;

  /**
   * Retrieves OFAC settings by association number (ACA number).
   */
  getSettingsBySourceAsync(acaNumber: string): Observable<OfacSettingModel | null>;

  /**
   * Retrieves association audit details for a specific source from the OFAC service.
   */
  getAssociationAuditDetailsAsync(sourceId: string): Observable<AuditDetailModel[]>;

  /**
   * Sends an OFAC status change message to the service bus for notification processing.
   */
  sendOfacStatusChangeMessageAsync(model: ServiceBusModel): Observable<void>;
}

// ==================== Client Implementation ====================

@Injectable({
  providedIn: 'root'
})
export class OfacMaintenanceClient implements IOfacMaintenanceClient {
  private http: HttpClient;
  private baseUrl: string;
  protected jsonParseReviver: ((key: string, value: any) => any) | undefined = undefined;

  private readonly auditController = 'Audit';
  private readonly exclusionController = 'Exclusion';
  private readonly applicationController = 'Application';
  private readonly ofacController = 'OFAC';
  private readonly flaggedContactsController = 'FlaggedContacts';
  private readonly settingsController = 'Settings';
  private readonly serviceBusController = 'ServiceBus';

  constructor(@Inject(HttpClient) http: HttpClient, @Optional() @Inject(API_BASE_URL) baseUrl?: string) {
    this.http = http;
    const url = baseUrl ?? environment.ofacService.url;
    // Ensure baseUrl ends with a trailing slash
    this.baseUrl = url.endsWith('/') ? url : url + '/';
  }

  /**
   * Logs an OFAC match result for audit and compliance tracking purposes.
   */
  logOfacMatchAsync(model: OfacAuditModel): Observable<OfacAuditModel> {
    let url_ = this.baseUrl + `${this.auditController}`;
    url_ = url_.replace(/[?&]$/, '');

    const content_ = JSON.stringify(model);

    let options_: any = {
      body: content_,
      observe: 'response',
      responseType: 'json',
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      })
    };

    return this.http.request('put', url_, options_).pipe(
      mergeMap((response_: any) => {
        return this.processLogOfacMatch(response_);
      }),
      catchError((response_: any) => {
        if (response_ instanceof HttpResponseBase) {
          try {
            return this.processLogOfacMatch(response_ as any);
          } catch (e) {
            return throwError(() => e) as any as Observable<OfacAuditModel>;
          }
        } else {
          return throwError(() => response_) as any as Observable<OfacAuditModel>;
        }
      })
    );
  }

  protected processLogOfacMatch(response: HttpResponseBase): Observable<OfacAuditModel> {
    const status = response.status;
    if (status === 201) {
      const responseText = response instanceof HttpResponse ? response.body : null;
      return of(responseText as OfacAuditModel);
    } else if (status !== 200 && status !== 204) {
      return throwException('An unexpected server error occurred.', status, '', response.headers);
    }
    return of(null as any);
  }

  /**
   * Retrieves all audit records from the OFAC service.
   */
  getAuditRecordsAsync(): Observable<OfacAuditModel[]> {
    let url_ = this.baseUrl + `${this.auditController}`;
    url_ = url_.replace(/[?&]$/, '');

    let options_: any = {
      observe: 'response',
      responseType: 'json',
      headers: new HttpHeaders({
        'Accept': 'application/json'
      })
    };

    return this.http.request('get', url_, options_).pipe(
      mergeMap((response_: any) => {
        return this.processGetAuditRecords(response_);
      }),
      catchError((response_: any) => {
        if (response_ instanceof HttpResponseBase) {
          try {
            return this.processGetAuditRecords(response_ as any);
          } catch (e) {
            return throwError(() => e) as any as Observable<OfacAuditModel[]>;
          }
        } else {
          return throwError(() => response_) as any as Observable<OfacAuditModel[]>;
        }
      })
    );
  }

  protected processGetAuditRecords(response: HttpResponseBase): Observable<OfacAuditModel[]> {
    const status = response.status;
    if (status === 200) {
      const responseText = response instanceof HttpResponse ? response.body : null;
      return of(responseText as OfacAuditModel[]);
    } else if (status !== 200 && status !== 204) {
      return throwException('An unexpected server error occurred.', status, '', response.headers);
    }
    return of([] as any);
  }

  /**
   * Retrieves audit records for a specific source from the OFAC service.
   */
  getAuditRecordsAsyncBySource(sourceId: string): Observable<OfacAuditModel[]> {
    if (sourceId === undefined || sourceId === null) {
      throw new Error('The parameter \'sourceId\' must be defined.');
    }
    let url_ = this.baseUrl + `${this.auditController}/Source/${encodeURIComponent('' + sourceId)}`;
    url_ = url_.replace(/[?&]$/, '');

    let options_: any = {
      observe: 'response',
      responseType: 'json',
      headers: new HttpHeaders({
        'Accept': 'application/json'
      })
    };

    return this.http.request('get', url_, options_).pipe(
      mergeMap((response_: any) => {
        return this.processGetAuditRecords(response_);
      }),
      catchError((response_: any) => {
        if (response_ instanceof HttpResponseBase) {
          try {
            return this.processGetAuditRecords(response_ as any);
          } catch (e) {
            return throwError(() => e) as any as Observable<OfacAuditModel[]>;
          }
        } else {
          return throwError(() => response_) as any as Observable<OfacAuditModel[]>;
        }
      })
    );
  }

  /**
   * Retrieves audit records for a specific source and application from the OFAC service.
   */
  getAuditRecordsAsyncBySourceAndApplication(sourceId: string, applicationId: string): Observable<OfacAuditModel[]> {
    if (sourceId === undefined || sourceId === null) {
      throw new Error('The parameter \'sourceId\' must be defined.');
    }
    if (applicationId === undefined || applicationId === null) {
      throw new Error('The parameter \'applicationId\' must be defined.');
    }
    let url_ = this.baseUrl + `${this.auditController}/Source/${encodeURIComponent('' + sourceId)}/Application/${encodeURIComponent('' + applicationId)}`;
    url_ = url_.replace(/[?&]$/, '');

    let options_: any = {
      observe: 'response',
      responseType: 'json',
      headers: new HttpHeaders({
        'Accept': 'application/json'
      })
    };

    return this.http.request('get', url_, options_).pipe(
      mergeMap((response_: any) => {
        return this.processGetAuditRecords(response_);
      }),
      catchError((response_: any) => {
        if (response_ instanceof HttpResponseBase) {
          try {
            return this.processGetAuditRecords(response_ as any);
          } catch (e) {
            return throwError(() => e) as any as Observable<OfacAuditModel[]>;
          }
        } else {
          return throwError(() => response_) as any as Observable<OfacAuditModel[]>;
        }
      })
    );
  }

  /**
   * Retrieves a specific exclusion record by identifier from the OFAC service.
   */
  getExclusionRecordAsync(id: string): Observable<OfacExclusionModel[]> {
    if (id === undefined || id === null) {
      throw new Error('The parameter \'id\' must be defined.');
    }
    let url_ = this.baseUrl + `${this.exclusionController}/${encodeURIComponent('' + id)}`;
    url_ = url_.replace(/[?&]$/, '');

    let options_: any = {
      observe: 'response',
      responseType: 'json',
      headers: new HttpHeaders({
        'Accept': 'application/json'
      })
    };

    return this.http.request('get', url_, options_).pipe(
      mergeMap((response_: any) => {
        return this.processGetExclusionRecords(response_);
      }),
      catchError((response_: any) => {
        if (response_ instanceof HttpResponseBase) {
          try {
            return this.processGetExclusionRecords(response_ as any);
          } catch (e) {
            return throwError(() => e) as any as Observable<OfacExclusionModel[]>;
          }
        } else {
          return throwError(() => response_) as any as Observable<OfacExclusionModel[]>;
        }
      })
    );
  }

  /**
   * Retrieves all exclusion records from the OFAC service.
   */
  getExclusionRecordsAsync(): Observable<OfacExclusionModel[]> {
    let url_ = this.baseUrl + `${this.exclusionController}`;
    url_ = url_.replace(/[?&]$/, '');

    let options_: any = {
      observe: 'response',
      responseType: 'json',
      headers: new HttpHeaders({
        'Accept': 'application/json'
      })
    };

    return this.http.request('get', url_, options_).pipe(
      mergeMap((response_: any) => {
        return this.processGetExclusionRecords(response_);
      }),
      catchError((response_: any) => {
        if (response_ instanceof HttpResponseBase) {
          try {
            return this.processGetExclusionRecords(response_ as any);
          } catch (e) {
            return throwError(() => e) as any as Observable<OfacExclusionModel[]>;
          }
        } else {
          return throwError(() => response_) as any as Observable<OfacExclusionModel[]>;
        }
      })
    );
  }

  /**
   * Retrieves exclusion records for a specific source from the OFAC service.
   */
  getExclusionRecordsAsyncBySource(sourceId: string): Observable<OfacExclusionModel[]> {
    if (sourceId === undefined || sourceId === null) {
      throw new Error('The parameter \'sourceId\' must be defined.');
    }
    let url_ = this.baseUrl + `${this.exclusionController}/Source/${encodeURIComponent('' + sourceId)}`;
    url_ = url_.replace(/[?&]$/, '');

    let options_: any = {
      observe: 'response',
      responseType: 'json',
      headers: new HttpHeaders({
        'Accept': 'application/json'
      })
    };

    return this.http.request('get', url_, options_).pipe(
      mergeMap((response_: any) => {
        return this.processGetExclusionRecords(response_);
      }),
      catchError((response_: any) => {
        if (response_ instanceof HttpResponseBase) {
          try {
            return this.processGetExclusionRecords(response_ as any);
          } catch (e) {
            return throwError(() => e) as any as Observable<OfacExclusionModel[]>;
          }
        } else {
          return throwError(() => response_) as any as Observable<OfacExclusionModel[]>;
        }
      })
    );
  }

  /**
   * Retrieves exclusion records for a specific source and application from the OFAC service.
   */
  getExclusionRecordsAsyncBySourceAndApplication(sourceId: string, applicationId: string): Observable<OfacExclusionModel[]> {
    if (sourceId === undefined || sourceId === null) {
      throw new Error('The parameter \'sourceId\' must be defined.');
    }
    if (applicationId === undefined || applicationId === null) {
      throw new Error('The parameter \'applicationId\' must be defined.');
    }
    let url_ = this.baseUrl + `${this.exclusionController}/Source/${encodeURIComponent('' + sourceId)}/Application/${encodeURIComponent('' + applicationId)}`;
    url_ = url_.replace(/[?&]$/, '');

    let options_: any = {
      observe: 'response',
      responseType: 'json',
      headers: new HttpHeaders({
        'Accept': 'application/json'
      })
    };

    return this.http.request('get', url_, options_).pipe(
      mergeMap((response_: any) => {
        return this.processGetExclusionRecords(response_);
      }),
      catchError((response_: any) => {
        if (response_ instanceof HttpResponseBase) {
          try {
            return this.processGetExclusionRecords(response_ as any);
          } catch (e) {
            return throwError(() => e) as any as Observable<OfacExclusionModel[]>;
          }
        } else {
          return throwError(() => response_) as any as Observable<OfacExclusionModel[]>;
        }
      })
    );
  }

  /**
   * Retrieves exclusion records for a specific source, application, and contact from the OFAC service.
   */
  getExclusionRecordsAsyncBySourceApplicationAndContact(sourceId: string, applicationId: string, contactId: string): Observable<OfacExclusionModel[]> {
    if (sourceId === undefined || sourceId === null) {
      throw new Error('The parameter \'sourceId\' must be defined.');
    }
    if (applicationId === undefined || applicationId === null) {
      throw new Error('The parameter \'applicationId\' must be defined.');
    }
    if (contactId === undefined || contactId === null) {
      throw new Error('The parameter \'contactId\' must be defined.');
    }
    let url_ = this.baseUrl + `${this.exclusionController}/Source/${encodeURIComponent('' + sourceId)}/Application/${encodeURIComponent('' + applicationId)}/Contact/${encodeURIComponent('' + contactId)}`;
    url_ = url_.replace(/[?&]$/, '');

    let options_: any = {
      observe: 'response',
      responseType: 'json',
      headers: new HttpHeaders({
        'Accept': 'application/json'
      })
    };

    return this.http.request('get', url_, options_).pipe(
      mergeMap((response_: any) => {
        return this.processGetExclusionRecords(response_);
      }),
      catchError((response_: any) => {
        if (response_ instanceof HttpResponseBase) {
          try {
            return this.processGetExclusionRecords(response_ as any);
          } catch (e) {
            return throwError(() => e) as any as Observable<OfacExclusionModel[]>;
          }
        } else {
          return throwError(() => response_) as any as Observable<OfacExclusionModel[]>;
        }
      })
    );
  }

  protected processGetExclusionRecords(response: HttpResponseBase): Observable<OfacExclusionModel[]> {
    const status = response.status;
    if (status === 200) {
      const responseText = response instanceof HttpResponse ? response.body : null;
      return of(responseText as OfacExclusionModel[]);
    } else if (status !== 200 && status !== 204) {
      return throwException('An unexpected server error occurred.', status, '', response.headers);
    }
    return of([] as any);
  }

  /**
   * Adds an exclusion record to the OFAC service for entity filtering.
   */
  addExclusionAsync(model: OfacExclusionModel): Observable<OfacExclusionModel> {
    let url_ = this.baseUrl + `${this.exclusionController}`;
    url_ = url_.replace(/[?&]$/, '');

    const content_ = JSON.stringify(model);

    let options_: any = {
      body: content_,
      observe: 'response',
      responseType: 'json',
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      })
    };

    return this.http.request('put', url_, options_).pipe(
      mergeMap((response_: any) => {
        return this.processAddExclusion(response_);
      }),
      catchError((response_: any) => {
        if (response_ instanceof HttpResponseBase) {
          try {
            return this.processAddExclusion(response_ as any);
          } catch (e) {
            return throwError(() => e) as any as Observable<OfacExclusionModel>;
          }
        } else {
          return throwError(() => response_) as any as Observable<OfacExclusionModel>;
        }
      })
    );
  }

  protected processAddExclusion(response: HttpResponseBase): Observable<OfacExclusionModel> {
    const status = response.status;
    if (status === 201) {
      const responseText = response instanceof HttpResponse ? response.body : null;
      return of(responseText as OfacExclusionModel);
    } else if (status !== 200 && status !== 204) {
      return throwException('An unexpected server error occurred.', status, '', response.headers);
    }
    return of(null as any);
  }

  /**
   * Deletes an exclusion record from the OFAC service.
   */
  deleteExclusionAsync(id: string): Observable<void> {
    if (id === undefined || id === null) {
      throw new Error('The parameter \'id\' must be defined.');
    }
    let url_ = this.baseUrl + `${this.exclusionController}/${encodeURIComponent('' + id)}`;
    url_ = url_.replace(/[?&]$/, '');

    let options_: any = {
      observe: 'response',
      responseType: 'json',
      headers: new HttpHeaders({})
    };

    return this.http.request('delete', url_, options_).pipe(
      mergeMap((response_: any) => {
        return this.processDeleteExclusion(response_);
      }),
      catchError((response_: any) => {
        if (response_ instanceof HttpResponseBase) {
          try {
            return this.processDeleteExclusion(response_ as any);
          } catch (e) {
            return throwError(() => e) as any as Observable<void>;
          }
        } else {
          return throwError(() => response_) as any as Observable<void>;
        }
      })
    );
  }

  protected processDeleteExclusion(response: HttpResponseBase): Observable<void> {
    const status = response.status;
    if (status === 200 || status === 204) {
      return of(null as any);
    } else if (status === 400) {
      return throwException('Failed to delete exclusion.', status, '', response.headers);
    } else if (status !== 200 && status !== 204) {
      return throwException('An unexpected server error occurred.', status, '', response.headers);
    }
    return of(null as any);
  }

  /**
   * Retrieves all application records from the OFAC service.
   */
  getApplicationRecordsAsync(): Observable<OfacApplicationModel[]> {
    let url_ = this.baseUrl + `${this.applicationController}`;
    url_ = url_.replace(/[?&]$/, '');

    let options_: any = {
      observe: 'response',
      responseType: 'json',
      headers: new HttpHeaders({
        'Accept': 'application/json'
      })
    };

    return this.http.request('get', url_, options_).pipe(
      mergeMap((response_: any) => {
        return this.processGetApplicationRecords(response_);
      }),
      catchError((response_: any) => {
        if (response_ instanceof HttpResponseBase) {
          try {
            return this.processGetApplicationRecords(response_ as any);
          } catch (e) {
            return throwError(() => e) as any as Observable<OfacApplicationModel[]>;
          }
        } else {
          return throwError(() => response_) as any as Observable<OfacApplicationModel[]>;
        }
      })
    );
  }

  protected processGetApplicationRecords(response: HttpResponseBase): Observable<OfacApplicationModel[]> {
    const status = response.status;
    if (status === 200) {
      const responseText = response instanceof HttpResponse ? response.body : null;
      return of(responseText as OfacApplicationModel[]);
    } else if (status !== 200 && status !== 204) {
      return throwException('An unexpected server error occurred.', status, '', response.headers);
    }
    return of([] as any);
  }

  /**
   * Retrieves a specific application record by identifier from the OFAC service.
   */
  getApplicationRecordAsync(id: string): Observable<OfacApplicationModel[]> {
    if (id === undefined || id === null) {
      throw new Error('The parameter \'id\' must be defined.');
    }
    let url_ = this.baseUrl + `${this.applicationController}/${encodeURIComponent('' + id)}`;
    url_ = url_.replace(/[?&]$/, '');

    let options_: any = {
      observe: 'response',
      responseType: 'json',
      headers: new HttpHeaders({
        'Accept': 'application/json'
      })
    };

    return this.http.request('get', url_, options_).pipe(
      mergeMap((response_: any) => {
        return this.processGetApplicationRecords(response_);
      }),
      catchError((response_: any) => {
        if (response_ instanceof HttpResponseBase) {
          try {
            return this.processGetApplicationRecords(response_ as any);
          } catch (e) {
            return throwError(() => e) as any as Observable<OfacApplicationModel[]>;
          }
        } else {
          return throwError(() => response_) as any as Observable<OfacApplicationModel[]>;
        }
      })
    );
  }

  /**
   * Adds an application record to the OFAC service.
   */
  addApplicationAsync(model: OfacApplicationModel): Observable<OfacApplicationModel> {
    let url_ = this.baseUrl + `${this.applicationController}`;
    url_ = url_.replace(/[?&]$/, '');

    const content_ = JSON.stringify(model);

    let options_: any = {
      body: content_,
      observe: 'response',
      responseType: 'json',
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      })
    };

    return this.http.request('put', url_, options_).pipe(
      mergeMap((response_: any) => {
        return this.processAddApplication(response_);
      }),
      catchError((response_: any) => {
        if (response_ instanceof HttpResponseBase) {
          try {
            return this.processAddApplication(response_ as any);
          } catch (e) {
            return throwError(() => e) as any as Observable<OfacApplicationModel>;
          }
        } else {
          return throwError(() => response_) as any as Observable<OfacApplicationModel>;
        }
      })
    );
  }

  protected processAddApplication(response: HttpResponseBase): Observable<OfacApplicationModel> {
    const status = response.status;
    if (status === 201) {
      const responseText = response instanceof HttpResponse ? response.body : null;
      return of(responseText as OfacApplicationModel);
    } else if (status !== 200 && status !== 204) {
      return throwException('An unexpected server error occurred.', status, '', response.headers);
    }
    return of(null as any);
  }

  /**
   * Deletes an application record from the OFAC service.
   */
  deleteApplicationAsync(id: string): Observable<void> {
    if (id === undefined || id === null) {
      throw new Error('The parameter \'id\' must be defined.');
    }
    let url_ = this.baseUrl + `${this.applicationController}/${encodeURIComponent('' + id)}`;
    url_ = url_.replace(/[?&]$/, '');

    let options_: any = {
      observe: 'response',
      responseType: 'json',
      headers: new HttpHeaders({})
    };

    return this.http.request('delete', url_, options_).pipe(
      mergeMap((response_: any) => {
        return this.processDeleteApplication(response_);
      }),
      catchError((response_: any) => {
        if (response_ instanceof HttpResponseBase) {
          try {
            return this.processDeleteApplication(response_ as any);
          } catch (e) {
            return throwError(() => e) as any as Observable<void>;
          }
        } else {
          return throwError(() => response_) as any as Observable<void>;
        }
      })
    );
  }

  protected processDeleteApplication(response: HttpResponseBase): Observable<void> {
    const status = response.status;
    if (status === 200 || status === 204) {
      return of(null as any);
    } else if (status === 400) {
      return throwException('Failed to delete application.', status, '', response.headers);
    } else if (status !== 200 && status !== 204) {
      return throwException('An unexpected server error occurred.', status, '', response.headers);
    }
    return of(null as any);
  }

  /**
   * Retrieves flagged contacts for a specific source from the OFAC service.
   */
  getFlaggedContactsAsync(sourceId: string): Observable<FlaggedContactModel[]> {
    if (sourceId === undefined || sourceId === null) {
      throw new Error('The parameter \'sourceId\' must be defined.');
    }
    let url_ = this.baseUrl + `${this.flaggedContactsController}/FlaggedContacts/${encodeURIComponent('' + sourceId)}`;
    url_ = url_.replace(/[?&]$/, '');

    let options_: any = {
      observe: 'response',
      responseType: 'json',
      headers: new HttpHeaders({
        'Accept': 'application/json'
      }),
      reportProgress: false
    };

    return this.http.request('get', url_, options_).pipe(
      mergeMap((response_: any) => this.processGetFlaggedContacts(response_)),
      catchError((response_: any) => {
        if (response_ instanceof HttpResponseBase) {
          try {
            return this.processGetFlaggedContacts(response_ as any);
          } catch (e) {
            return throwError(() => e) as any as Observable<FlaggedContactModel[]>;
          }
        }
        return throwError(() => response_) as any as Observable<FlaggedContactModel[]>;
      })
    );
  }

  protected processGetFlaggedContacts(response: HttpResponseBase): Observable<FlaggedContactModel[]> {
    const status = response.status;
    if (status === 200) {
      const responseText = response instanceof HttpResponse ? response.body : null;
      let dataArray: any[] = [];

      if (responseText === null || responseText === undefined) {
        return of([]);
      }

      if (!Array.isArray(responseText)) {
        if (responseText && typeof responseText === 'object') {
          if ('data' in responseText) {
            const data = (responseText as any).data;
            dataArray = Array.isArray(data) ? data : [];
          } else if ('value' in responseText) {
            const value = (responseText as any).value;
            dataArray = Array.isArray(value) ? value : [];
          } else if ('items' in responseText) {
            const items = (responseText as any).items;
            dataArray = Array.isArray(items) ? items : [];
          } else {
            dataArray = [responseText];
          }
        } else {
          return of([]);
        }
      } else {
        dataArray = responseText;
      }

      const normalized = dataArray.map((item: any, index: number) => {
        if (!item || typeof item !== 'object') {
          return item;
        }
        const keys = Object.keys(item);
        const hasPascalCase = keys.some(key => key.charAt(0) === key.charAt(0).toUpperCase() && key !== key.toLowerCase());
        if (hasPascalCase) {
          const normalizedItem: any = {};
          for (const key of keys) {
            if (item.hasOwnProperty(key)) {
              const camelKey = key.charAt(0).toLowerCase() + key.slice(1);
              normalizedItem[camelKey] = item[key];
            }
          }
          return normalizedItem;
        }
        return item;
      });

      return of(normalized as FlaggedContactModel[]);
    }
    if (status !== 200 && status !== 204) {
      return throwException('An unexpected server error occurred.', status, '', response.headers);
    }
    return of([] as any);
  }

  /**
   * Retrieves flagged contact exclusions for a specific source from the OFAC service.
   */
  getFlaggedContactExclusionsAsync(sourceId: string): Observable<FlaggedContactModel[]> {
    if (sourceId === undefined || sourceId === null) {
      throw new Error('The parameter \'sourceId\' must be defined.');
    }
    let url_ = this.baseUrl + `${this.flaggedContactsController}/FlaggedContactExclusions/${encodeURIComponent('' + sourceId)}`;
    url_ = url_.replace(/[?&]$/, '');

    let options_: any = {
      observe: 'response',
      responseType: 'json',
      headers: new HttpHeaders({
        'Accept': 'application/json'
      })
    };

    return this.http.request('get', url_, options_).pipe(
      mergeMap((response_: any) => {
        return this.processGetFlaggedContacts(response_);
      }),
      catchError((response_: any) => {
        if (response_ instanceof HttpResponseBase) {
          try {
            return this.processGetFlaggedContacts(response_ as any);
          } catch (e) {
            return throwError(() => e) as any as Observable<FlaggedContactModel[]>;
          }
        } else {
          return throwError(() => response_) as any as Observable<FlaggedContactModel[]>;
        }
      })
    );
  }

  /**
   * Retrieves non-excluded OFAC match detail rows for a specific source and contact from the OFAC service.
   */
  getFlaggedContactDetailsAsync(sourceId: string, contactId: string): Observable<FlaggedContactDetailModel[]> {
    if (sourceId === undefined || sourceId === null) {
      throw new Error('The parameter \'sourceId\' must be defined.');
    }
    if (contactId === undefined || contactId === null) {
      throw new Error('The parameter \'contactId\' must be defined.');
    }
    let url_ = this.baseUrl + `${this.flaggedContactsController}/FlaggedContactDetails/${encodeURIComponent('' + sourceId)}?contactId=${encodeURIComponent('' + contactId)}`;
    url_ = url_.replace(/[?&]$/, '');

    let options_: any = {
      observe: 'response',
      responseType: 'json',
      headers: new HttpHeaders({
        'Accept': 'application/json'
      })
    };

    return this.http.request('get', url_, options_).pipe(
      mergeMap((response_: any) => {
        return this.processGetFlaggedContactDetails(response_);
      }),
      catchError((response_: any) => {
        if (response_ instanceof HttpResponseBase) {
          try {
            return this.processGetFlaggedContactDetails(response_ as any);
          } catch (e) {
            return throwError(() => e) as any as Observable<FlaggedContactDetailModel[]>;
          }
        } else {
          return throwError(() => response_) as any as Observable<FlaggedContactDetailModel[]>;
        }
      })
    );
  }

  /**
   * Retrieves excluded OFAC match detail rows for a specific source and contact from the OFAC service.
   */
  getExcludedContactDetailsAsync(sourceId: string, contactId: string): Observable<FlaggedContactDetailModel[]> {
    if (sourceId === undefined || sourceId === null) {
      throw new Error('The parameter \'sourceId\' must be defined.');
    }
    if (contactId === undefined || contactId === null) {
      throw new Error('The parameter \'contactId\' must be defined.');
    }
    let url_ = this.baseUrl + `${this.flaggedContactsController}/ExcludedContactDetails/${encodeURIComponent('' + sourceId)}?contactId=${encodeURIComponent('' + contactId)}`;
    url_ = url_.replace(/[?&]$/, '');

    let options_: any = {
      observe: 'response',
      responseType: 'json',
      headers: new HttpHeaders({
        'Accept': 'application/json'
      })
    };

    return this.http.request('get', url_, options_).pipe(
      mergeMap((response_: any) => {
        return this.processGetFlaggedContactDetails(response_);
      }),
      catchError((response_: any) => {
        if (response_ instanceof HttpResponseBase) {
          try {
            return this.processGetFlaggedContactDetails(response_ as any);
          } catch (e) {
            return throwError(() => e) as any as Observable<FlaggedContactDetailModel[]>;
          }
        } else {
          return throwError(() => response_) as any as Observable<FlaggedContactDetailModel[]>;
        }
      })
    );
  }

  protected processGetFlaggedContactDetails(response: HttpResponseBase): Observable<FlaggedContactDetailModel[]> {
    const status = response.status;
    if (status === 200) {
      const responseText = response instanceof HttpResponse ? response.body : null;
      return of(responseText as FlaggedContactDetailModel[]);
    } else if (status !== 200 && status !== 204) {
      return throwException('An unexpected server error occurred.', status, '', response.headers);
    }
    return of([] as any);
  }

  /**
   * Retrieves the date when the OFAC database was last loaded from external sources.
   */
  getLastLoadDateAsync(): Observable<Date> {
    let url_ = this.baseUrl + `${this.ofacController}/GetLastLoadDate`;
    url_ = url_.replace(/[?&]$/, '');

    let options_: any = {
      observe: 'response',
      responseType: 'json',
      headers: new HttpHeaders({
        'Accept': 'application/json'
      })
    };

    return this.http.request('get', url_, options_).pipe(
      mergeMap((response_: any) => {
        return this.processGetLastLoadDate(response_);
      }),
      catchError((response_: any) => {
        if (response_ instanceof HttpResponseBase) {
          try {
            return this.processGetLastLoadDate(response_ as any);
          } catch (e) {
            return throwError(() => e) as any as Observable<Date>;
          }
        } else {
          return throwError(() => response_) as any as Observable<Date>;
        }
      })
    );
  }

  protected processGetLastLoadDate(response: HttpResponseBase): Observable<Date> {
    const status = response.status;
    if (status === 200) {
      const responseText = response instanceof HttpResponse ? response.body : null;
      return of(new Date(responseText as string));
    } else if (status !== 200 && status !== 204) {
      return throwException('An unexpected server error occurred.', status, '', response.headers);
    }
    return of(null as any);
  }

  /**
   * Retrieves the total count of OFAC records currently stored in the database.
   */
  getRecordCountAsync(): Observable<number> {
    let url_ = this.baseUrl + `${this.ofacController}/GetRecordCount`;
    url_ = url_.replace(/[?&]$/, '');

    let options_: any = {
      observe: 'response',
      responseType: 'json',
      headers: new HttpHeaders({
        'Accept': 'application/json'
      })
    };

    return this.http.request('get', url_, options_).pipe(
      mergeMap((response_: any) => {
        return this.processGetRecordCount(response_);
      }),
      catchError((response_: any) => {
        if (response_ instanceof HttpResponseBase) {
          try {
            return this.processGetRecordCount(response_ as any);
          } catch (e) {
            return throwError(() => e) as any as Observable<number>;
          }
        } else {
          return throwError(() => response_) as any as Observable<number>;
        }
      })
    );
  }

  protected processGetRecordCount(response: HttpResponseBase): Observable<number> {
    const status = response.status;
    if (status === 200) {
      const responseText = response instanceof HttpResponse ? response.body : null;
      return of(responseText as number);
    } else if (status !== 200 && status !== 204) {
      return throwException('An unexpected server error occurred.', status, '', response.headers);
    }
    return of(0 as any);
  }

  /**
   * Refreshes the OFAC database by loading the latest sanctions information from external sources.
   */
  refreshDataBaseAsync(): Observable<void> {
    let url_ = this.baseUrl + `${this.ofacController}/RefreshOFAC`;
    url_ = url_.replace(/[?&]$/, '');

    let options_: any = {
      observe: 'response',
      responseType: 'json',
      headers: new HttpHeaders({})
    };

    return this.http.request('post', url_, options_).pipe(
      mergeMap((response_: any) => {
        return this.processRefreshDatabase(response_);
      }),
      catchError((response_: any) => {
        if (response_ instanceof HttpResponseBase) {
          try {
            return this.processRefreshDatabase(response_ as any);
          } catch (e) {
            return throwError(() => e) as any as Observable<void>;
          }
        } else {
          return throwError(() => response_) as any as Observable<void>;
        }
      })
    );
  }

  protected processRefreshDatabase(response: HttpResponseBase): Observable<void> {
    const status = response.status;
    if (status === 204) {
      return of(null as any);
    } else if (status !== 200 && status !== 204) {
      return throwException('An unexpected server error occurred.', status, '', response.headers);
    }
    return of(null as any);
  }

  /**
   * Retrieves all OFAC settings.
   */
  getAllSettingsAsync(): Observable<OfacSettingModel[]> {
    let url_ = this.baseUrl + `${this.settingsController}`;
    url_ = url_.replace(/[?&]$/, '');

    let options_: any = {
      observe: 'response',
      responseType: 'json',
      headers: new HttpHeaders({
        'Accept': 'application/json'
      })
    };

    return this.http.request('get', url_, options_).pipe(
      mergeMap((response_: any) => this.processGetAllSettings(response_)),
      catchError((error: any) => {
        if (error instanceof HttpResponseBase) {
          try {
            return this.processGetAllSettings(error as any);
          } catch (e) {
            return throwError(() => e) as any as Observable<OfacSettingModel[]>;
          }
        }
        return throwError(() => error) as any as Observable<OfacSettingModel[]>;
      })
    );
  }

  protected processGetAllSettings(response: HttpResponseBase): Observable<OfacSettingModel[]> {
    const status = response.status;
    if (status === 200) {
      const responseText = response instanceof HttpResponse ? response.body : null;
      if (responseText && Array.isArray(responseText)) {
        return of(responseText as OfacSettingModel[]);
      }
      if (responseText) {
        return of([responseText] as OfacSettingModel[]);
      }
      return of([]);
    }
    if (status !== 200 && status !== 204) {
      const errorMsg = `An unexpected server error occurred. Status: ${status}`;
      return throwException(errorMsg, status, '', response.headers);
    }
    return of([] as any);
  }

  /**
   * Retrieves OFAC settings by association number (ACA number).
   */
  getSettingsBySourceAsync(acaNumber: string): Observable<OfacSettingModel | null> {
    if (acaNumber === undefined || acaNumber === null || acaNumber === '') {
      throw new Error('The parameter \'acaNumber\' must be defined.');
    }
    let url_ = this.baseUrl + `${this.settingsController}/${encodeURIComponent('' + acaNumber)}`;
    url_ = url_.replace(/[?&]$/, '');

    let options_: any = {
      observe: 'response',
      responseType: 'json',
      headers: new HttpHeaders({
        'Accept': 'application/json'
      })
    };

    return this.http.request('get', url_, options_).pipe(
      mergeMap((response_: any) => {
        return this.processGetSettingsBySource(response_);
      }),
      catchError((response_: any) => {
        if (response_ instanceof HttpResponseBase) {
          try {
            return this.processGetSettingsBySource(response_ as any);
          } catch (e) {
            return throwError(() => e) as any as Observable<OfacSettingModel | null>;
          }
        } else {
          return throwError(() => response_) as any as Observable<OfacSettingModel | null>;
        }
      })
    );
  }

  /**
   * Retrieves association audit details for a specific source from the OFAC service.
   */
  getAssociationAuditDetailsAsync(sourceId: string): Observable<AuditDetailModel[]> {
    if (sourceId === undefined || sourceId === null || sourceId === '') {
      throw new Error('The parameter \'sourceId\' must be defined.');
    }
    let url_ = this.baseUrl + `${this.flaggedContactsController}/AuditDetails/${encodeURIComponent('' + sourceId)}`;
    url_ = url_.replace(/[?&]$/, '');

    let options_: any = {
      observe: 'response',
      responseType: 'json',
      headers: new HttpHeaders({
        'Accept': 'application/json'
      })
    };

    return this.http.request('get', url_, options_).pipe(
      mergeMap((response_: any) => this.processGetAssociationAuditDetails(response_)),
      catchError((error: any) => {
        if (error instanceof HttpResponseBase) {
          try {
            return this.processGetAssociationAuditDetails(error as any);
          } catch (e) {
            return throwError(() => e) as any as Observable<AuditDetailModel[]>;
          }
        }
        return throwError(() => error) as any as Observable<AuditDetailModel[]>;
      })
    );
  }

  protected processGetAssociationAuditDetails(response: HttpResponseBase): Observable<AuditDetailModel[]> {
    const status = response.status;
    if (status === 200) {
      const responseText = response instanceof HttpResponse ? response.body : null;
      if (responseText && Array.isArray(responseText)) {
        const mapped = responseText.map((item: any) => ({
          ...item,
          createdOn: item.createdOn ? new Date(item.createdOn) : null
        }));
        return of(mapped as AuditDetailModel[]);
      }
      if (responseText) {
        const mapped = {
          ...responseText,
          createdOn: responseText.createdOn ? new Date(responseText.createdOn) : null
        };
        return of([mapped] as AuditDetailModel[]);
      }
      return of([]);
    }
    if (status !== 200 && status !== 204) {
      const errorMsg = `An unexpected server error occurred. Status: ${status}`;
      return throwException(errorMsg, status, '', response.headers);
    }
    return of([] as any);
  }

  protected processGetSettingsBySource(response: HttpResponseBase): Observable<OfacSettingModel | null> {
    const status = response.status;
    if (status === 200) {
      const responseText = response instanceof HttpResponse ? response.body : null;
      return of(responseText as OfacSettingModel);
    } else if (status === 404) {
      return of(null);
    } else if (status !== 200 && status !== 204) {
      return throwException('An unexpected server error occurred.', status, '', response.headers);
    }
    return of(null);
  }

  /**
   * Sends an OFAC status change message to the service bus for notification processing.
   */
  sendOfacStatusChangeMessageAsync(model: ServiceBusModel): Observable<void> {
    if (model === undefined || model === null) {
      throw new Error('The parameter \'model\' must be defined.');
    }

    let url_ = this.baseUrl + `${this.serviceBusController}/SendOfacStatusChangeMessage`;
    url_ = url_.replace(/[?&]$/, '');

    const content_ = JSON.stringify(model);

    let options_: any = {
      body: content_,
      observe: 'response',
      responseType: 'json',
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      })
    };

    return this.http.request('post', url_, options_).pipe(
      mergeMap((response_: any) => {
        return this.processSendOfacStatusChangeMessage(response_);
      }),
      catchError((response_: any) => {
        if (response_ instanceof HttpResponseBase) {
          try {
            return this.processSendOfacStatusChangeMessage(response_ as any);
          } catch (e) {
            return throwError(() => e) as any as Observable<void>;
          }
        } else {
          return throwError(() => response_) as any as Observable<void>;
        }
      })
    );
  }

  protected processSendOfacStatusChangeMessage(response: HttpResponseBase): Observable<void> {
    const status = response.status;
    if (status === 200 || status === 204) {
      return of(void 0);
    }
    return throwException('An unexpected server error occurred.', status, '', response.headers);
  }
}

// ==================== Exception Helper ====================

function throwException(message: string, status: number, response: string, headers: { [key: string]: any; }): Observable<never> {
  if (status !== 0 && status === 200) {
    return throwError(() => new Error(message));
  } else {
    return throwError(() => new Error(message || `Error Code: ${status}\nMessage: ${response}`));
  }
}
